import { useMemo, useReducer } from "react";
import { useTranslation } from "react-i18next";
import { Game, GameDraggableId } from "../../Games";
import { GameDraggable } from "../../components/GameDraggable";
import { GameDroppable } from "../../components/GameDroppable";
import { DndGame, DndGameActions, DndGameDroppable } from "./DndGame";
import { DndGameDraggable } from "./components/DndGameDraggable";
import { DndGameDroppableZone } from "./components/DndGameDroppableZone";

export const useDndGameReducer = ({
  current,
}: {
  current: Game | undefined;
}) => {
  //  CONTEXTS
  const { t } = useTranslation();

  //  STATES
  const [data, dispatch] = useReducer(
    (data: DndGame, action: DndGameActions): DndGame => {
      switch (action.type) {
        case "dropTo":
          return {
            ...data,
            droppables: data.droppables.map((drop) => {
              if (drop.id === action.to)
                return { ...drop, have: action.drag as GameDraggableId };
              else return drop;
            }),
          };
        case "switch":
          return {
            ...data,
            droppables: data.droppables.map((drop) => {
              if (drop.id === action.to)
                return { ...drop, have: action.drag as GameDraggableId };
              else if (drop.id === action.from)
                return {
                  ...drop,
                  have: data.droppables.find((drop) => drop.id === action.to)
                    ?.have as GameDraggableId,
                };
              else return drop;
            }),
          };
        case "reset":
          return {
            ...data,
            droppables: data.droppables.map((drop) => {
              if (drop.have === action.id) return { ...drop, have: "" };
              else return drop;
            }),
          };
        case "validate":
          const validItems = data.droppables.reduce(
            (acc, drop) => {
              if (!drop.want.includes(drop.have ?? "")) acc.isValid = false;
              else acc.validCount += 1;
              return acc;
            },
            { isValid: true, validCount: 0 }
          );
          return {
            ...data,
            isValid: validItems.validCount === data.draggables.length,
            progress: validItems.validCount / data.draggables.length,
          };
        default:
          return { ...data };
      }
    },
    {
      ...current,
      isValid: false,
      progress: 0,
      draggables: [...(current as DndGame).draggables].sort(
        (a, b) => Math.random() - 0.5
      ),
    } as DndGame
  );

  //  DATAS
  const draggables = useMemo(
    () =>
      data.draggables
        .filter(
          (drag) => !data.droppables.find((drop) => drop.have === drag.id)
        )
        .map((drag) => (
          <DndGameDraggable
            key={drag.id}
            id={drag.id}
            hostedBy=""
            disabled={data.isValid ?? false}
          >
            {t(`games.${data.id}.${drag.id}`)}
          </DndGameDraggable>
        )),
    [data.draggables, data.droppables, data.isValid, t, data.id]
  );

  const droppables = useMemo(() => {
    const categories = data.droppables.reduce<{
      [k: string]: DndGameDroppable[];
    }>(
      (acc, drop) => ({ ...acc, [drop.cat]: [...(acc[drop.cat] ?? []), drop] }),
      {}
    );

    return Object.keys(categories).map((cat) => (
      <DndGameDroppableZone
        key={cat}
        categorie={cat}
        gameId={data.id as string}
      >
        {categories[cat].map((drop) => {
          return (
            <GameDroppable key={drop.id} id={drop.id} want={drop.want}>
              {drop.have !== "" && (
                <GameDraggable
                  key={drop.id}
                  id={drop.have}
                  hostedBy={drop.id}
                  disabled={data.isValid ?? false}
                >
                  {t(`games.${data.id}.${drop.have}`)}
                </GameDraggable>
              )}
            </GameDroppable>
          );
        })}
      </DndGameDroppableZone>
    ));
  }, [data.droppables, data.isValid, t, data.id]);

  return { dispatch, draggables, droppables, data };
};
